/**
 * Module dependencies.
 */

import 'react-popper-tooltip/dist/styles.css';
import { ReactNode, useCallback, useRef } from 'react';
import { Config, usePopperTooltip } from 'react-popper-tooltip';
import { Transition, TransitionStatus } from 'react-transition-group';
import { RawHtml } from 'src/components/core/raw-html';
import { Text } from 'src/components/core/text';
import styled from 'styled-components';
import { switchProp } from 'styled-tools';

/**
 * `Props` type.
 */

type Props = {
  children: ReactNode;
  className?: string;
  description?: string;
  title?: string;
} & Config;

/**
 * `TriggerElement` styled component.
 */

const TriggerElement = styled.span`
  cursor: pointer;
`;

/**
 * `TooltipElement` styled component.
 */

const TooltipElement = styled.div<{ transitionStatus: TransitionStatus }>`
  background-color: var(--color-grey800);
  border-radius: 8px;
  box-shadow: var(--box-shadow-default);
  color: var(--color-white);
  display: flex;
  flex-direction: column;
  opacity: 0;
  padding: 16px;
  transition: opacity var(--transition-default);
  white-space: normal;
  width: 240px;
  z-index: var(--z-index-tooltip);

  ${switchProp('transitionStatus', {
    entered: `
      opacity: 1;
    `,
    entering: `
      opacity: 1;
    `,
    exited: `
      opacity: 0;
    `,
    exiting: `
      opacity: 0;
    `
  })}
`;

/**
 * Export `Tooltip` component.
 */

export const Tooltip = (props: Props) => {
  const { children, className, description, title, ...options } = props;
  const { getTooltipProps, setTooltipRef, setTriggerRef, visible } = usePopperTooltip(options);
  const nodeRef = useRef<HTMLDivElement | null>(null);
  const setRefs = useCallback(
    (element: HTMLDivElement | null) => {
      nodeRef.current = element;
      setTooltipRef(element);
    },
    [setTooltipRef]
  );

  return (
    <>
      <TriggerElement className={className} ref={setTriggerRef}>
        {children}
      </TriggerElement>

      <Transition in={visible} mountOnEnter nodeRef={nodeRef} timeout={1000} unmountOnExit>
        {(state: TransitionStatus) => (
          <TooltipElement ref={setRefs} transitionStatus={state} {...getTooltipProps()}>
            {title && (
              <Text fontWeight={700} style={{ marginBottom: description ? 8 : 0 }} variant={'paragraph1'}>
                {title}
              </Text>
            )}

            {description && (
              <Text variant={'paragraph3'}>
                <RawHtml>{description}</RawHtml>
              </Text>
            )}
          </TooltipElement>
        )}
      </Transition>
    </>
  );
};
